<template>
  <v-dialog
    :model-value="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    max-width="1200"
    width="90vw"
  >
    <v-card rounded="xl" class="detail-card">
      <v-toolbar density="comfortable" class="detail-toolbar" flat height="100">
        <v-toolbar-title
          class="text-subtitle-1 font-weight-bold d-flex align-center flex-wrap custom-toolbar-title"
        >
          <v-icon class="ml-2 mr-2" color="primary" size="20">
            mdi-clipboard-text-outline
          </v-icon>
          <div class="title-scroll">
            <span class="mr-1" style="font-size: 90%">
              {{ projectName ? projectName + " - " : "" }}
            </span>

            <span
              class="editable-title"
              :contenteditable="isEditing"
              spellcheck="false"
              ref="titleEl"
              style="font-size: 90%"
              @keydown.enter.prevent="finishTitleEdit"
              @blur="finishTitleEdit"
            >
              {{ localTitle || "Detalle del caso" }}
            </span>
          </div>
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          v-if="isEditing"
          icon
          variant="text"
          :disabled="!canUndo"
          title="Deshacer (Ctrl+Z)"
          @click="undo"
        >
          <v-icon>mdi-undo</v-icon>
        </v-btn>

        <v-btn
          v-if="isEditing"
          icon
          variant="text"
          :disabled="!canRedo"
          title="Rehacer (Ctrl+Y)"
          @click="redo"
        >
          <v-icon>mdi-redo</v-icon>
        </v-btn>

        <v-btn
          v-if="isEditing"
          icon
          variant="text"
          color="warning"
          title="Revertir cambios"
          @click="revertChanges"
        >
          <v-icon>mdi-restore</v-icon>
        </v-btn>

        <v-btn
          icon
          variant="text"
          @click="toggleEdit"
          :title="isEditing ? 'Guardar' : 'Editar'"
        >
          <v-icon>{{ isEditing ? "mdi-content-save" : "mdi-pencil" }}</v-icon>
        </v-btn>

        <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text class="pa-5">
        <div class="mb-4">
          <div class="text-caption text-medium-emphasis mb-1">Descripción</div>

          <v-card variant="outlined" rounded="lg" class="cool-card desc-box">
            <v-card-text class="pa-3">
              <v-textarea
                v-model="localDescription"
                :disabled="!isEditing"
                density="compact"
                variant="outlined"
                hide-details
                rows="6"
                auto-grow
                class="desc-input"
                @blur="finishDescEdit"
              />
            </v-card-text>
          </v-card>

          <div v-if="isEditing" class="text-caption text-medium-emphasis mt-2">
            {{ $t("tip2") }}
          </div>
        </div>

        <div class="d-flex align-center justify-space-between mb-2">
          <div class="text-caption text-medium-emphasis">Steps</div>

          <div class="d-flex align-center ga-2">
            <v-chip size="x-small" variant="tonal" color="primary">
              {{ localSteps.length }} steps
            </v-chip>

            <v-btn
              v-if="isEditing"
              size="small"
              variant="tonal"
              prepend-icon="mdi-plus"
              @click="addStep"
            >
              {{ $t("msg13") }}
            </v-btn>
          </div>
        </div>

        <v-card variant="outlined" rounded="lg" class="cool-card table-card">
          <div ref="stepsWrap" class="table-scroll-x">
            <v-data-table
              :headers="headers"
              :items="localSteps"
              density="compact"
              class="pro-table steps-table"
              item-key="step"
              hide-default-footer
              :sort-by="[]"
              disable-sort
              :items-per-page="-1"
            >
              <template #item="{ index }">
                <tr>
                  <td style="width: 70px">
                    <v-chip size="x-small" variant="outlined" color="primary">
                      {{ localSteps[index]?.step ?? index + 1 }}
                    </v-chip>
                  </td>

                  <td>
                    <v-textarea
                      :model-value="localSteps[index]?.action ?? ''"
                      @update:modelValue="onStepUpdate(index, 'action', $event)"
                      @blur="onStepBlur(index, 'action')"
                      :disabled="!isEditing"
                      density="compact"
                      variant="outlined"
                      hide-details
                      rows="4"
                      auto-grow
                      class="step-input"
                      :ref="(el) => setActionRef(el, index)"
                    />
                  </td>

                  <td>
                    <v-textarea
                      :model-value="localSteps[index]?.expected ?? ''"
                      @update:modelValue="
                        onStepUpdate(index, 'expected', $event)
                      "
                      @blur="onStepBlur(index, 'expected')"
                      :disabled="!isEditing"
                      density="compact"
                      variant="outlined"
                      hide-details
                      rows="4"
                      auto-grow
                      class="step-input"
                    />
                  </td>

                  <td v-if="isEditing" style="width: 70px; text-align: right">
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="removeStep(index)"
                      :title="
                        'Eliminar step ' +
                        (localSteps[index]?.step ?? index + 1)
                      "
                    >
                      <v-icon style="color: #b71c1c">mdi-delete-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4 justify-end">
        <v-btn variant="outlined" @click="$emit('update:modelValue', false)">
          Cerrar
        </v-btn>

        <v-btn v-if="isEditing" variant="tonal" @click="saveAll">
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "TestCaseDetailDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    testCase: { type: Object, default: null },
    projectName: { type: String, default: "" },
  },
  emits: ["update:modelValue", "save"],
  data() {
    return {
      isEditing: false,
      localTitle: "",
      localDescription: "",
      localSteps: [],
      originalSnapshot: null,
      history: [],
      historyIndex: -1,
      historyTimer: null,
      actionRefs: {},
    };
  },
  computed: {
    headers() {
      const base = [
        { title: "#", key: "step", width: 70, sortable: false },
        { title: "Acción", key: "action", sortable: false },
        { title: "Esperado", key: "expected", sortable: false },
      ];
      return this.isEditing
        ? [...base, { title: "", key: "actions", width: 70, sortable: false }]
        : base;
    },
    canUndo() {
      return this.isEditing && this.historyIndex > 0;
    },
    canRedo() {
      return (
        this.isEditing &&
        this.historyIndex >= 0 &&
        this.historyIndex < this.history.length - 1
      );
    },
  },
  watch: {
    testCase: {
      immediate: true,
      handler(v) {
        this.localTitle = (v?.title || "").toString();
        this.localDescription = (v?.description || "").toString();
        const steps = Array.isArray(v?.steps) ? v.steps : [];
        this.localSteps = steps.map((s, i) => ({
          step: s?.step ?? i + 1,
          action: (s?.action ?? "").toString(),
          expected: (s?.expected ?? "").toString(),
        }));
      },
    },
    modelValue(val) {
      if (val) {
        this.$nextTick(() => this.syncEditableDom());
        this.addHotkeys();
      } else {
        this.removeHotkeys();
        this.isEditing = false;
        this.clearHistory();
      }
    },
    isEditing(val) {
      if (val) this.addHotkeys();
      else this.removeHotkeys();
    },
  },
  beforeUnmount() {
    this.removeHotkeys();
    this.clearHistoryTimers();
  },
  methods: {
    setActionRef(el, index) {
      if (el) this.actionRefs[index] = el;
    },

    addHotkeys() {
      this.removeHotkeys();
      window.addEventListener("keydown", this.onGlobalKeydown, {
        passive: false,
      });
    },

    removeHotkeys() {
      window.removeEventListener("keydown", this.onGlobalKeydown);
    },

    onGlobalKeydown(e) {
      if (!this.modelValue || !this.isEditing) return;

      const key = (e.key || "").toLowerCase();
      const meta = e.ctrlKey || e.metaKey;

      if (meta && key === "s") {
        e.preventDefault();
        this.saveAll();
        return;
      }

      if (meta && key === "z" && !e.shiftKey) {
        e.preventDefault();
        this.undo();
        return;
      }

      if (meta && (key === "y" || (key === "z" && e.shiftKey))) {
        e.preventDefault();
        this.redo();
      }
    },

    toggleEdit() {
      this.isEditing = !this.isEditing;

      this.$nextTick(() => {
        if (this.isEditing) {
          this.originalSnapshot = this.makeSnapshot();
          this.resetHistoryWithCurrent();
          this.syncEditableDom();
          this.focusTitleEnd();
        } else {
          this.saveAll();
        }
      });
    },

    makeSnapshot() {
      return JSON.parse(
        JSON.stringify({
          title: this.localTitle,
          description: this.localDescription,
          steps: this.localSteps,
        }),
      );
    },

    applySnapshot(snap) {
      if (!snap) return;
      this.localTitle = (snap.title || "").toString();
      this.localDescription = (snap.description || "").toString();
      const steps = Array.isArray(snap.steps) ? snap.steps : [];
      this.localSteps = steps.map((s, i) => ({
        step: s?.step ?? i + 1,
        action: (s?.action ?? "").toString(),
        expected: (s?.expected ?? "").toString(),
      }));
      this.$nextTick(() => this.syncEditableDom());
    },

    resetHistoryWithCurrent() {
      this.clearHistoryTimers();
      this.history = [this.makeSnapshot()];
      this.historyIndex = 0;
    },

    clearHistory() {
      this.history = [];
      this.historyIndex = -1;
      this.originalSnapshot = null;
      this.clearHistoryTimers();
    },

    clearHistoryTimers() {
      if (this.historyTimer) {
        clearTimeout(this.historyTimer);
        this.historyTimer = null;
      }
    },

    scheduleHistoryPush(delay = 250) {
      if (!this.isEditing) return;
      this.clearHistoryTimers();
      this.historyTimer = setTimeout(() => this.pushHistoryNow(), delay);
    },

    pushHistoryNow() {
      if (!this.isEditing) return;

      const snap = this.makeSnapshot();
      const current = this.history[this.historyIndex];
      const same = current && JSON.stringify(current) === JSON.stringify(snap);
      if (same) return;

      if (this.historyIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyIndex + 1);
      }

      this.history.push(snap);
      this.historyIndex = this.history.length - 1;
    },

    undo() {
      if (!this.canUndo) return;
      this.pushHistoryNow();
      this.historyIndex = Math.max(0, this.historyIndex - 1);
      this.applySnapshot(this.history[this.historyIndex]);
    },

    redo() {
      if (!this.canRedo) return;
      this.pushHistoryNow();
      this.historyIndex = Math.min(
        this.history.length - 1,
        this.historyIndex + 1,
      );
      this.applySnapshot(this.history[this.historyIndex]);
    },

    revertChanges() {
      if (!this.originalSnapshot) return;
      this.applySnapshot(this.originalSnapshot);
      this.resetHistoryWithCurrent();
    },

    syncEditableDom() {
      if (this.$refs.titleEl)
        this.$refs.titleEl.innerText = this.localTitle || "Detalle del caso";
    },

    focusTitleEnd() {
      const el = this.$refs.titleEl;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    },

    finishTitleEdit() {
      if (!this.isEditing) return;
      const el = this.$refs.titleEl;
      const text = (el?.innerText || "").trim();
      this.localTitle = text;
      this.pushHistoryNow();
    },

    finishDescEdit() {
      if (!this.isEditing) return;
      this.localDescription = (this.localDescription ?? "")
        .toString()
        .replace(/\r\n/g, "\n")
        .trimEnd();
      this.pushHistoryNow();
    },

    onStepUpdate(index, field, val) {
      if (!this.isEditing) return;
      const updated = [...this.localSteps];
      if (!updated[index]) return;
      updated[index] = { ...updated[index], [field]: (val ?? "").toString() };
      this.localSteps = updated;
      this.scheduleHistoryPush();
    },

    onStepBlur(index, field) {
      if (!this.isEditing) return;
      const updated = [...this.localSteps];
      if (!updated[index]) return;
      updated[index] = {
        ...updated[index],
        [field]: (updated[index][field] ?? "").toString().trimEnd(),
      };
      this.localSteps = updated;
      this.pushHistoryNow();
    },

    addStep() {
      const next = (this.localSteps?.length || 0) + 1;
      this.localSteps = [
        ...this.localSteps,
        { step: next, action: "", expected: "" },
      ];
      this.pushHistoryNow();

      this.$nextTick(() => {
        const last = this.localSteps.length - 1;
        const ref = this.actionRefs[last];
        if (ref?.focus) ref.focus();
        else if (ref?.$el?.querySelector)
          ref.$el.querySelector("input")?.focus?.();
      });
    },

    removeStep(index) {
      const updated = [...this.localSteps];
      updated.splice(index, 1);
      this.localSteps = updated.map((s, i) => ({ ...s, step: i + 1 }));
      this.pushHistoryNow();
      this.$nextTick(() => {
        this.actionRefs = {};
      });
    },

    saveAll() {
      this.finishTitleEdit();
      this.finishDescEdit();
      this.pushHistoryNow();

      this.$emit("save", {
        ...this.testCase,
        title: this.localTitle,
        description: this.localDescription,
        steps: this.localSteps.map((s, i) => ({
          step: s?.step ?? i + 1,
          action: (s?.action ?? "").toString().trimEnd(),
          expected: (s?.expected ?? "").toString().trimEnd(),
        })),
      });
    },
  },
};
</script>

<style scoped>
.detail-card {
  border-radius: 18px;
}
.detail-toolbar {
  background: transparent;
}

.editable-title {
  outline: none;
  border-radius: 8px;
  padding: 2px 6px;
}

.editable-title[contenteditable="true"] {
  border: 1px dashed rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.05);
}

.desc-box {
  border-radius: 14px;
}
.desc-input :deep(.v-field) {
  border-radius: 12px;
}

.steps-table :deep(.v-data-table__td),
.steps-table :deep(.v-data-table__th) {
  vertical-align: top;
}

.step-input :deep(.v-field) {
  border-radius: 10px;
}
.custom-toolbar-title {
  white-space: normal;
}
.desc-input :deep(textarea) {
  max-height: 320px;
  overflow-y: auto;
  resize: none;
}

.step-input :deep(textarea) {
  max-height: 260px;
  overflow-y: auto;
  resize: none;
}

.editable-title {
  white-space: normal;
  word-break: break-word;
  font-size: 20px; /* más grande */
  line-height: 1.4;
  min-width: 0;
}
.title-scroll {
  max-height: 60px;
  overflow-y: auto;
  word-break: break-word;
}
.table-card {
  border-radius: 14px;
  overflow: hidden;
}

.table-scroll-x {
  max-height: 1000px;
  overflow-y: auto;
  word-break: break-word;
}

.pro-table {
  min-width: 950px;
}
</style>
